import { useMemo, useState } from 'react';
import type { IDatasource, IGetRowsParams } from 'ag-grid-community';

/**
 * TD-PAGINATION (2026-06-07): серверна пагінація для ag-Grid Infinite Row Model.
 *
 * Перетворює backend list-endpoint (limit/offset/total) на ag-Grid IDatasource.
 * ag-Grid тягне рядки сторінками по скролу — браузер не вантажить весь
 * довідник (десятки тисяч рядків) одразу.
 *
 * Використання:
 * ```tsx
 * const { datasource, total } = useInfiniteGridDatasource(
 *   (limit, offset) => nomenclature.list({ q, kind_id, limit, offset }),
 *   [q, kind_id],  // deps — зміна пересоздае datasource (ag-Grid перетягне)
 * );
 * <AgGridReact rowModelType="infinite" datasource={datasource} ... />
 * ```
 *
 * `total` оновлюється після кожної сторінки (для лічильника «N записів»).
 */
export interface Page<T> {
  items: T[];
  total: number;
}

/** Серверна сортировка, що передається у fetchPage (3-й аргумент, опц.). */
export interface SortSpec {
  sort_by: string;            // ag-Grid colId (= field колонки)
  sort_dir: 'asc' | 'desc';
}

export function useInfiniteGridDatasource<T>(
  fetchPage: (limit: number, offset: number, sort?: SortSpec) => Promise<Page<T>>,
  deps: ReadonlyArray<unknown>,
): { datasource: IDatasource; total: number | null } {
  const [total, setTotal] = useState<number | null>(null);

  // Пересоздаемо datasource при зміні deps (пошук/фільтр) → ag-Grid скидае
  // кеш і тягне з нуля. fetchPage навмисно НЕ у deps (замикання на свіже
  // значення через React-rerender; deps керують ідентичністю).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const datasource = useMemo<IDatasource>(() => ({
    async getRows(params: IGetRowsParams) {
      const limit = params.endRow - params.startRow;
      // Infinite Row Model НЕ сортує локально — лише передає sortModel при
      // кліку на заголовок. Сортувати має сервер (sort_by/sort_dir).
      const sm = params.sortModel?.[0];
      const sort: SortSpec | undefined =
        sm && (sm.sort === 'asc' || sm.sort === 'desc')
          ? { sort_by: sm.colId, sort_dir: sm.sort }
          : undefined;
      try {
        const page = await fetchPage(limit, params.startRow, sort);
        setTotal(page.total);
        // lastRow = загальна кількість, коли вона відома → ag-Grid знае межу.
        const lastRow =
          page.items.length < limit
            ? params.startRow + page.items.length
            : page.total;
        params.successCallback(page.items, lastRow);
      } catch {
        params.failCallback();
      }
    },
  }), deps);

  return { datasource, total };
}
